<template>
  <b-modal v-model="model" centered modal-class="modal-bb-squared" hide-footer>
    <template slot="modal-header" slot-scope="{ close }">
      <h5>
        Unlock your dedicated recruiter. Guaranteed hire. No cure, no pay.
      </h5>
      <p class="fs15 text-gray-700 pt-10">
        The following is required and will only be shared with our team.
      </p>
      <button class="close" @click="close"></button>
    </template>

    <template slot="default">
      <form @submit.prevent="onSubmit">
        <div style="display: flex;">
          <FormItem
            class="mb-10"
            style="width: 100%;"
            invalid-message="Please enter full name"
            :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
            required
          >
            <b-form-input
              v-model="form.name"
              maxlength="100"
              :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
              placeholder="Full name"
            />
          </FormItem>
          <div style="width: 1.2rem;"></div>
          <FormItem
            class="mb-10"
            style="width: 100%;"
            invalid-message="Please enter company name"
            :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
            required
          >
            <b-form-input
              v-model="form.company"
              maxlength="100"
              :state="$v.form.company.$dirty ? !$v.form.company.$error : null"
              placeholder="Company name"
            />
          </FormItem>
        </div>

        <FormItem class="mb-10">
          <b-form-input v-model="form.title" placeholder="Job title" />
        </FormItem>

        <FormItem
          class="mb-10"
          invalid-message="Please enter email"
          :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
          required
        >
          <b-form-input
            v-model="form.email"
            :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
            placeholder="E-mail address"
          />
        </FormItem>

        <FormItem
          class="mb-10"
          invalid-message="Please enter description"
          :state="$v.form.message.$dirty ? !$v.form.message.$error : null"
          required
        >
          <template slot="description">
            <FormInputLengthCounter
              label="Maximum 300 characters"
              exceeded-label="Keep it short, no one has time to read a novel"
              exceeded-label-color="#FA6400"
              :max="300"
              :text="form.message"
            />
          </template>

          <template>
            <b-form-textarea
              v-model="form.message"
              rows="3"
              :max-rows="8"
              placeholder="What are you looking to hire? Give a short description."
              :state="$v.form.message.$dirty ? !$v.form.message.$error : null"
              @blur="$v.form.message.$touch()"
            />
          </template>
        </FormItem>

        <!-- <FormItem class="mt-20">
          <b-form-checkbox v-model="form.updates">
            <div class="fs14 mt-1 text-gray-700">
              Send me email updates with similar positions
            </div>
          </b-form-checkbox>
        </FormItem> -->

        <FormItem
          class="my-30"
          :state="$v.form.reCaptchaResponse.$dirty ? !$v.form.reCaptchaResponse.$error : null"
          invalid-message="Please verify you are not a robot"
        >
          <VueRecaptcha
            ref="reCaptcha"
            :sitekey="siteKey"
            :load-recaptcha-script="true"
            @verify="onVerify"
            @expired="onExpired"
          />
        </FormItem>

        <AlertSlideUpDown :show="!!errorMessage">
          {{ errorMessage }}
        </AlertSlideUpDown>

        <b-button type="submit" class="btn btn-lg w-100 mb-20">
          <span v-if="!isRequesting">Send request</span>
          <b-spinner v-else small />
        </b-button>

        <div class="fs14 mt-10">
          By sending the request you confirm that you accept our
          <router-link to="/terms" target="_blank" class="text-blue-900"
            >Terms of Service</router-link
          >
        </div>
      </form>
    </template>
  </b-modal>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import { validationMixin } from 'vuelidate';
import { required, email } from 'vuelidate/lib/validators';
// import { linkedinUrlValidator, urlValidator } from '@/validators/urlValidator'
import FormItem from '@/components/form/FormItem';
import FormInputLengthCounter from '@/components/form/FormInputLengthCounter';
import AlertSlideUpDown from '@/components/AlertSlideUpDown';
import debounce from 'lodash/debounce';
// import SlideUpDown from 'vue-slide-up-down'
// import FormUploadFile from '@/components/form/FormUploadFile/index'

const initial = {
  name: undefined,
  email: undefined,
  title: undefined,
  message: undefined,
  company: undefined,
  reCaptchaResponse: undefined
};

export default {
  components: {
    VueRecaptcha,
    AlertSlideUpDown,
    FormItem,
    FormInputLengthCounter
    // FormUploadFile,
    // SlideUpDown
  },
  mixins: [validationMixin],
  props: {
    value: {
      required: true,
      type: Boolean
    },
    action: {
      required: false,
      type: Function
    }
  },
  data: () => ({
    siteKey: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
    isRequesting: false,
    errorMessage: '',

    form: {
      ...initial
    }
  }),
  computed: {
    user() {
      return this.$store.getters['user/data'];
    },
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  watch: {
    value() {
      if (!this.value) {
        // on modal hide, reset re-captcha
        this.$refs.reCaptcha.reset();
      }
    }
  },
  validations() {
    return {
      form: {
        email: {
          required,
          email
        },
        name: {
          required
        },
        company: {
          required
        },
        message: { required },
        reCaptchaResponse: {
          required
        }
      }
    };
  },
  mounted() {
    if (this.user) {
      this.fillUserData();
    }
  },
  methods: {
    fillUserData() {
      this.form.email = this.user.email;
      this.form.name = this.user.fullName;
      this.form.phone = this.user.applicant.phone;
      this.form.title = this.user.applicant.position;
    },
    onSubmit: debounce(
      async function() {
        if (this.isRequesting) {
          return;
        }

        this.$v.form.$touch(); // will trigger errors to appear.

        console.log('this.$v.form.$error', this.$v.form.$error);
        if (this.$v.form.$error) {
          return;
        }

        this.isRequesting = true;
        this.errorMessage = undefined;

        const formData = new FormData();

        for (const name in this.form) {
          if (this.form[name]) {
            if (name === 'cv' || name === 'video') {
              this.form[name] && formData.append(name, this.form[name].id);
            } else {
              formData.append(
                name,
                Array.isArray(this.form[name]) ? JSON.stringify(this.form[name]) : this.form[name]
              );
            }
          }
        }

        try {
          this.action && (await this.action(this.form));
        } catch (err) {
          this.errorMessage = err.message;
        }

        setTimeout(() => {
          if (this.$refs.reCaptcha) {
            this.$refs.reCaptcha.reset();
          }
        }, 1000);

        this.isRequesting = false;
      },
      1000,
      {
        leading: true,
        trailing: false
      }
    ),
    onVerify(response) {
      this.form.reCaptchaResponse = response;
      this.$v.form.reCaptchaResponse.$touch();
    },
    onExpired() {
      this.form.reCaptchaResponse = undefined;
      this.$v.form.reCaptchaResponse.$touch();
    }
  }
};
</script>
<style scoped lang="scss">
.form-item {
  &__error {
    font-size: 0.85rem;
    color: $red;
    text-align: left;
    opacity: 0.9;

    &__inner {
      padding-top: 0.5rem;
    }
  }
}
</style>
